import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import { Option, Permissions, Season } from 'types';

import { useAppSelector } from 'redux/hooks';

import { budgetDataService } from 'services/budget.service';
import { usersDataService } from 'services/users.service';

import { mapSeasons, sortSeasons } from 'helpers/mapSeasons';

const fileNameFromDisposition = (disposition?: string): string | undefined =>
  disposition?.match(/filename="?([^";]+)"?/)?.[1];

const saveFile = (data: Blob, fileName: string) => {
  const url = URL.createObjectURL(data);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const seasonLabel = (season: string, budgets: number) => {
  if (!budgets) return `${season} · no closed budgets`;

  return `${season} · ${budgets} ${budgets === 1 ? 'budget' : 'budgets'}`;
};

const useClosedBudgetExport = () => {
  const isAdmin = useAppSelector((state) =>
    Boolean(state.currentUser.user?.permissions.includes(Permissions.ADMIN))
  );

  const [seasons, seasonsSet] = useState<Season[]>([]);
  const [season, seasonSet] = useState<string>();
  const [isLoading, isLoadingSet] = useState(false);
  // closed budgets per season; undefined until loaded (or if the request fails)
  const [closedCounts, closedCountsSet] = useState<Record<string, number>>();

  const loadClosedCounts = useCallback(() => {
    budgetDataService
      .getClosedSeasons()
      .then((res) =>
        closedCountsSet(
          Object.fromEntries(res.data.map((item) => [item.season, item.budgets]))
        )
      )
      .catch(() => {
        // the http-common interceptor already shows the error toast
      });
  }, []);

  useEffect(() => {
    if (isAdmin) {
      usersDataService.getSeasons().then((res) => seasonsSet(res.data));
      loadClosedCounts();
    }
  }, [isAdmin, loadClosedCounts]);

  // Newest season first; one option per season code (the API returns one row per catalogue).
  // Seasons without closed budgets are shown but cannot be picked; without the
  // counts every season stays selectable and the download reports an empty one.
  const seasonOptions: (Option & { isDisabled?: boolean })[] = useMemo(
    () =>
      mapSeasons(sortSeasons([...seasons])).map((item) => {
        if (!closedCounts) return { value: item.season, label: item.season };

        const budgets = closedCounts[item.season] ?? 0;

        return {
          value: item.season,
          label: seasonLabel(item.season, budgets),
          isDisabled: budgets === 0,
        };
      }),
    [seasons, closedCounts]
  );

  const download = async () => {
    if (!season) {
      toast.error('Please select a season');
      return;
    }

    isLoadingSet(true);

    try {
      const res = await budgetDataService.exportClosed(season);

      if (res.status === 204) {
        toast(`No closed budget data for season ${season}`);
        return;
      }

      saveFile(
        res.data,
        fileNameFromDisposition(res.headers['content-disposition']) ||
          `closed_budget_${season}.csv`
      );
    } catch {
      // the http-common interceptor already shows the error toast
    } finally {
      isLoadingSet(false);
    }
  };

  return {
    isAdmin,
    seasonOptions,
    seasonSet,
    download,
    isLoading,
    refreshClosedCounts: loadClosedCounts,
  };
};

export { useClosedBudgetExport };
